import { useEffect, useRef, useState } from "react"

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  // Base: warm cream canvas
  {
    elementType: "geometry",
    stylers: [{ color: "#f0ece6" }],
  },
  {
    elementType: "labels.text.fill",
    stylers: [{ color: "#3a3a3c" }],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [{ color: "#f5f2ed" }, { weight: 2.5 }],
  },

  // Administrative boundaries
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#d6d0c8" }],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },

  // Roads: stone palette
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#e8e4df" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#dbd6cf" }],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{ color: "#ddd8d1" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#cec8c0" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.local",
    elementType: "labels",
    stylers: [{ visibility: "simplified" }],
  },

  // Water: cool muted blue-grey
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#b8ccd4" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8aa8b4" }],
  },

  // Parks: muted sage
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#d3ddc9" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8a9a7c" }],
  },

  // Hide most POIs for a clean look
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.government",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.medical",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.school",
    elementType: "geometry",
    stylers: [{ color: "#e6e2db" }],
  },
  {
    featureType: "poi.sports_complex",
    elementType: "geometry",
    stylers: [{ color: "#d3ddc9" }],
  },
  {
    featureType: "poi.attraction",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.place_of_worship",
    stylers: [{ visibility: "off" }],
  },

  // Buildings: subtle warm fill
  {
    featureType: "landscape.man_made",
    elementType: "geometry.fill",
    stylers: [{ color: "#ebe7e1" }],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry.fill",
    stylers: [{ color: "#e8e4dc" }],
  },

  // Transit: hidden
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
]

// AdvancedMarkerElement pin (requires mapId)
function createMarkerContent(): HTMLElement {
  const pin = new google.maps.marker.PinElement({
    background: "#C41E2A",
    borderColor: "#ffffff",
    glyphColor: "#ffffff",
    scale: 1.3,
  })
  return pin.element
}

// Legacy Marker icon (fallback when no mapId)
function createLegacyMarkerIcon(): google.maps.Symbol {
  return {
    path: "M12 0C7.03 0 3 4.03 3 9c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9zm0 12.75c-2.07 0-3.75-1.68-3.75-3.75S9.93 5.25 12 5.25 15.75 6.93 15.75 9 14.07 12.75 12 12.75z",
    fillColor: "#C41E2A",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.8,
    anchor: new google.maps.Point(12, 24),
  }
}

let loadPromise: Promise<void> | null = null

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve()
  if (loadPromise) return loadPromise

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=marker`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      loadPromise = null
      reject(new Error("Failed to load Google Maps"))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

export default function ListingMap({
  latitude,
  longitude,
  address,
  apiKey,
  mapId,
}: {
  latitude: number
  longitude: number
  address: string
  apiKey: string | null
  mapId?: string | null
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!apiKey || !containerRef.current) return

    loadGoogleMaps(apiKey)
      .then(() => {
        if (!containerRef.current) return

        const position = { lat: latitude, lng: longitude }

        const map = new google.maps.Map(containerRef.current, {
          center: position,
          zoom: 15,
          styles: MAP_STYLES,
          disableDefaultUI: true,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          gestureHandling: "cooperative",
          backgroundColor: "#f5f2ed",
          ...(mapId ? { mapId } : {}),
        })

        if (mapId) {
          new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            content: createMarkerContent(),
            title: address,
          })
        } else {
          new google.maps.Marker({
            position,
            map,
            icon: createLegacyMarkerIcon(),
            title: address,
          })
        }

        mapRef.current = map
      })
      .catch(() => setError(true))
  }, [latitude, longitude, apiKey, address, mapId])

  if (!apiKey || error) return null

  return (
    <div className="mb-8 overflow-hidden rounded-xl border border-stone/40">
      <div className="relative">
        {/* Map container */}
        <div
          ref={containerRef}
          className="h-72 w-full sm:h-80 lg:h-96"
          style={{ backgroundColor: "#f5f2ed" }}
        />

        {/* Address overlay */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/85 via-charcoal/60 to-transparent px-5 pb-4 pt-12">
          <div className="flex items-center gap-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red shadow-lg shadow-red/25">
              <svg className="size-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{address}</p>
              <p className="text-xs text-white/50">
                {latitude.toFixed(5)}, {longitude.toFixed(5)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
